import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  cancelDepositCheckRequest,
  fetchDepositCheckRequests,
  resolveDepositCheckRequest,
} from "../../services/orders";
import { getInvoicePermissionState } from "../../utils/invoicePermissions";
import { hasDepositCheckRequests } from "./depositCheckVisibility";

const REQUEST_STATUSES = {
  pending: { label: "Pending", color: "warning" },
  confirmed: { label: "Confirmed", color: "success" },
  not_found: { label: "Not Found", color: "error" },
  cancelled: { label: "Cancelled", color: "default" },
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const formatCurrency = (amount, currency = "CAD") => {
  const parsed = Number(amount);
  if (!Number.isFinite(parsed)) return "—";
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: String(currency || "CAD").toUpperCase(),
    }).format(parsed);
  } catch (_error) {
    return `${String(currency || "CAD").toUpperCase()} ${parsed.toFixed(2)}`;
  }
};

const summarizeRecipients = (recipients = []) =>
  recipients
    .map((recipient) => recipient?.name || recipient?.email || `User ${recipient?.id}`)
    .filter(Boolean)
    .join(", ");

const toStatusChip = (status) => {
  const config = REQUEST_STATUSES[String(status || "").toLowerCase()] || {
    label: status || "Unknown",
    color: "default",
  };
  return <Chip size="small" label={config.label} color={config.color} variant="outlined" />;
};

export default function DepositCheckPanel({
  instanceUniqueId,
  orderId,
  currentUser,
  variant = "finance",
  onRequestsChange,
}) {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [routing, setRouting] = useState({ configured: false, recipients: [], warnings: [] });
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [resolveAction, setResolveAction] = useState("confirmed");
  const [financeResponse, setFinanceResponse] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const invoicePermissions = useMemo(
    () => getInvoicePermissionState(currentUser || {}),
    [currentUser]
  );
  const canManageInvoices = invoicePermissions.canManageInvoices;
  const canViewInvoices = invoicePermissions.canViewInvoices;
  const allowResolve = variant !== "sales" && canManageInvoices;

  const syncRequests = useCallback(
    (nextRequests) => {
      setRequests(nextRequests);
      if (typeof onRequestsChange === "function") {
        onRequestsChange(nextRequests);
      }
    },
    [onRequestsChange]
  );

  const loadRequests = useCallback(async () => {
    if (!instanceUniqueId || !orderId) return;
    setLoading(true);
    try {
      const response = await fetchDepositCheckRequests(orderId, instanceUniqueId);
      syncRequests(Array.isArray(response?.requests) ? response.requests : []);
      setRouting(response?.routing || { configured: false, recipients: [], warnings: [] });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load Deposit Check requests.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId, orderId, syncRequests]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const openResolveDialog = (request, action) => {
    setSelectedRequest(request);
    setResolveAction(action);
    setFinanceResponse(action === "not_found" ? "" : request?.financeResponse || "");
    setResolveError("");
    setResolveDialogOpen(true);
  };

  const closeResolveDialog = () => {
    if (submitting) return;
    setResolveDialogOpen(false);
    setSelectedRequest(null);
    setResolveAction("confirmed");
    setFinanceResponse("");
  };

  const replaceRequest = (nextRequest) => {
    const nextRequests = requests.some((request) => request.id === nextRequest.id)
      ? requests.map((request) => (request.id === nextRequest.id ? nextRequest : request))
      : [nextRequest, ...requests];
    syncRequests(nextRequests);
  };

  const handleResolveRequest = async () => {
    if (!selectedRequest?.id) return;
    const responseText = String(financeResponse || "").trim();
    if (resolveAction === "not_found" && !responseText) {
      setResolveError("Add a response so Sales knows why the payment could not be confirmed.");
      return;
    }

    setSubmitting(true);
    setResolveError("");
    try {
      const response =
        resolveAction === "cancelled"
          ? await cancelDepositCheckRequest(orderId, selectedRequest.id, {
              instanceUniqueId,
              financeResponse: responseText,
            })
          : await resolveDepositCheckRequest(orderId, selectedRequest.id, {
              instanceUniqueId,
              status: resolveAction,
              financeResponse: responseText,
            });

      replaceRequest(response.request);
      toast.success(
        resolveAction === "confirmed"
          ? "Deposit Check confirmed."
          : resolveAction === "not_found"
            ? "Deposit Check marked as not found."
            : "Deposit Check cancelled."
      );
      closeResolveDialog();
    } catch (error) {
      setResolveError(
        error?.response?.data?.message || "Unable to update the Deposit Check request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sortedRequests = useMemo(
    () =>
      [...requests].sort((a, b) => {
        const aTime = new Date(a?.createdAt || 0).getTime();
        const bTime = new Date(b?.createdAt || 0).getTime();
        return bTime - aTime;
      }),
    [requests]
  );

  if (!allowResolve && !canViewInvoices) {
    return null;
  }

  if (!hasDepositCheckRequests(sortedRequests)) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, display: "grid", gap: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <div>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Deposit Check
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review offline-payment verification requests. Confirming a request does not
            record payment.
          </Typography>
        </div>
        <Button variant="outlined" onClick={loadRequests} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </Stack>

      {routing?.warnings?.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}

      <Table size="small" aria-label="Deposit Check requests">
        <TableHead>
          <TableRow>
            <TableCell>Requested</TableCell>
            <TableCell>Payment</TableCell>
            <TableCell>Requester</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Recipients</TableCell>
            <TableCell>Response</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRequests.length ? (
            sortedRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Typography variant="body2">{formatDateTime(request.createdAt)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {request.amount != null
                      ? `${request.paymentMethodLabel} • ${formatCurrency(
                          request.amount,
                          request.currency
                        )}`
                      : request.paymentMethodLabel}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {request.requestNote}
                  </Typography>
                </TableCell>
                <TableCell>
                  {request.invoiceId ? `Invoice #${request.invoiceId}` : "Order-level request"}
                </TableCell>
                <TableCell>{request?.requestedByUser?.name || "Unknown"}</TableCell>
                <TableCell>{toStatusChip(request.status)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {summarizeRecipients(request.recipients) || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {request.financeResponse || "—"}
                  </Typography>
                  {request.resolvedByUser ? (
                    <Typography variant="caption" color="text.secondary">
                      {request.resolvedByUser.name} • {formatDateTime(request.resolvedAt)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  {allowResolve && request.status === "pending" ? (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="flex-end"
                      spacing={1}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => openResolveDialog(request, "confirmed")}
                      >
                        Confirm
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => openResolveDialog(request, "not_found")}
                      >
                        Not Found
                      </Button>
                    </Stack>
                  ) : null}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No Deposit Check requests yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={resolveDialogOpen} onClose={closeResolveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resolveAction === "confirmed"
            ? "Confirm Deposit Check"
            : resolveAction === "not_found"
              ? "Mark Deposit Check as Not Found"
              : "Cancel Deposit Check"}
        </DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 2, pt: 2 }}>
          {resolveError ? <Alert severity="error">{resolveError}</Alert> : null}
          <Alert severity={resolveAction === "confirmed" ? "info" : "warning"}>
            {resolveAction === "confirmed"
              ? "Confirmed means Finance found evidence of the payment. The invoice must still be updated through the normal payment controls."
              : resolveAction === "not_found"
                ? "No payment will be recorded and Sales Order approval will not change."
                : "Cancelling the request leaves payment handling unchanged."}
          </Alert>
          <TextField
            label="Finance response"
            multiline
            minRows={3}
            fullWidth
            required={resolveAction === "not_found"}
            value={financeResponse}
            onChange={(event) => setFinanceResponse(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResolveDialog} disabled={submitting}>
            Close
          </Button>
          <Button variant="contained" onClick={handleResolveRequest} disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
